import { useMatchRoute } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { memo, useMemo } from 'react';

import { guard, merge } from '@/components';
import { ErrorCard, Heading, Save, Separator, Table, Text } from '@/components/core';
import { ExportCell, ExportOption, EXPORTS } from '@/components/flow';
import { DivProps, Id, OmitChildren } from '@/utils';

export type OnExportOptionSelected = (exportOption: ExportOption) => void;

export type NewExportProps = OmitChildren<DivProps> & {
  onExportOptionSelected?: OnExportOptionSelected;
  isAddingExport?: boolean;
  errors?: Error[];
};

const INewExport = ({ onExportOptionSelected, isAddingExport, className, errors, ...rest }: NewExportProps) => {
  const matchRoute = useMatchRoute();
  const includeFakes = matchRoute({ to: '/agents/new' });

  const exportData: (ExportOption & Id)[] = useMemo(() => {
    return EXPORTS.filter((e) => (!includeFakes ? !e.isFakeExport : true)).map((e) => ({ ...e, id: e.functionId }));
  }, [includeFakes]);

  const exportColumns = useMemo<ColumnDef<ExportOption & Id>[]>(() => {
    return [
      {
        accessorKey: 'functionId',
        cell: ({ row }) => (
          <ExportCell
            onPress={() => onExportOptionSelected?.(row.original)}
            option={row.original}
            isDisabled={!!row.original.badge}
          />
        ),
        size: Number.MAX_SAFE_INTEGER,
      },
    ];
  }, [onExportOptionSelected]);

  return (
    <div className={merge('gap-md p-md flex flex-col', className)} {...rest}>
      <div className="gap-xs flex flex-col">
        <Heading slot="title">New export</Heading>
        <Text>Where are your documents going?</Text>
      </div>
      <Separator orientation="horizontal" />
      {guard(isAddingExport, () => (
        <Save message="Setting up export" />
      ))}
      {errors ? (
        <div className="h-fit">
          <ErrorCard label="Failed to add export" error={errors} />
        </div>
      ) : null}
      {/* TODO: Maybe use ternary statement instead */}
      {guard(!isAddingExport, () => (
        <div className="overflow-y-auto">
          <Table
            className="border-separate border-spacing-2"
            columns={exportColumns}
            data={exportData}
            hideHeader
            hideDragHandle
            hideRowOptions
          />
        </div>
      ))}
    </div>
  );
};

export const NewExport = memo(INewExport) as typeof INewExport;
